import { useMutation, useSuspenseQuery } from '@apollo/client';
import startCase from 'lodash/startCase';
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import {
  Content,
  DebugJson,
  Drawer,
  DrawerContent,
  DrawerTrigger,
  HStack,
  Header,
  Heading,
  InlineEdit,
  NewButton,
  PageTitle,
  Separator,
  SidebarTrigger,
  Tag,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@eluve/components';
import { TemplateVariantCard } from '@eluve/frontend-feature-prompt-templates';
import { LlmOutputTypesLookup } from '@eluve/graphql-types';
import { FragmentOf } from '@eluve/graphql.tada';
import {
  convertDynamicArtifactToJsonSchema,
  dynamicArtifactTemplateSchema,
  hydrateDynamicArtifactTemplatePreview,
} from '@eluve/llm-outputs';
import {
  useIsEluveAdmin,
  useIsPromptTemplateCreator,
  useIsTemplatePublisher,
} from '@eluve/session-helpers';
import { DynamicArtifactRenderer } from '@eluve/smart-blocks';
import { formatHumanName } from '@eluve/utils';

import {
  getOutputTemplateQuery,
  setOutputTemplateAsDefaultMutation,
  templateVariantFragment,
  updateOutputTemplateMutation,
  updateVariantNotesMutation,
} from './operations';
import { TemplatePublishingDialog } from './tenant-specific/TemplatePublishingDialog';
import { useIsOrganizationRoute } from './useIsOrganizationRoute';
import { useOutputTemplateRoutePath } from './useOutputTemplateRoutePath';

export const OutputTemplateDetailsPage: React.FC = () => {
  const toast = useToast();
  const { outputTemplateId, variantNumber } = useParams() as {
    outputTemplateId: string;
    variantNumber?: string;
  };
  const [selectedVariantNumber, setSelectedVariantNumber] = useState<
    number | null
  >(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const navigate = useNavigate();

  const isEluveAdmin = useIsEluveAdmin();
  const isTemplateCreator = useIsPromptTemplateCreator();
  const isTemplatePublisher = useIsTemplatePublisher();

  const isOrganizationRoute = useIsOrganizationRoute();

  const {
    outputTemplateBasePath,
    createCloneOutputVariantPath,
    createEditVariantPath,
  } = useOutputTemplateRoutePath();

  const {
    data: { llmOutputTemplatesByPk: outputTemplateData },
  } = useSuspenseQuery(getOutputTemplateQuery, {
    variables: {
      id: outputTemplateId,
    },
  });

  const [setOutputTemplateAsDefault] = useMutation(
    setOutputTemplateAsDefaultMutation,
    {
      context: isEluveAdmin ? eluveAdminHasuraContext : undefined,
      onError: () => {
        toast.error('Variant could not be set as the default.');
      },
    },
  );

  const [updateOutputTemplate] = useMutation(updateOutputTemplateMutation, {
    context: isEluveAdmin ? eluveAdminHasuraContext : undefined,
    onError: () => {
      toast.error('Template could not be updated.');
      setIsUpdating(false);
    },
    onCompleted: () => {
      setIsUpdating(false);
    },
  });

  const [updateVariantNotes] = useMutation(updateVariantNotesMutation, {
    context: isEluveAdmin ? eluveAdminHasuraContext : undefined,
    onError: () => {
      toast.error('Variant notes could not be updated.');
    },
  });

  const selectedVariantTemplate = useMemo(() => {
    let variantTemplate: FragmentOf<typeof templateVariantFragment> | null =
      null;

    if (variantNumber) {
      variantTemplate =
        outputTemplateData?.variants.find(
          (variant) => variant.variantNumber === Number(variantNumber),
        ) ?? null;
    }

    // If variant number is not specified in the URL, fall back to the current default variant
    variantTemplate ??=
      outputTemplateData?.variants.find(
        (variant) => variant.isCurrentDefault,
      ) ?? null;

    // If there's no current default, fall back to the latest variant
    variantTemplate ??=
      outputTemplateData?.variants
        .slice()
        .sort((a, b) => b.variantNumber - a.variantNumber)[0] ?? null;

    if (!variantTemplate) {
      return undefined;
    }

    const parsed = dynamicArtifactTemplateSchema.safeParse(
      variantTemplate?.template,
    );
    setSelectedVariantNumber(variantTemplate.variantNumber);

    if (!parsed.success) {
      return undefined;
    }

    return parsed.data;
  }, [outputTemplateData?.variants, variantNumber]);

  const handleUpdateName = async (newName: string) => {
    setIsUpdating(true);
    await updateOutputTemplate({
      variables: {
        id: outputTemplateId,
        name: newName,
        description: outputTemplateData?.description,
      },
    });
  };

  const handleUpdateDescription = async (newDescription: string) => {
    setIsUpdating(true);
    await updateOutputTemplate({
      variables: {
        id: outputTemplateId,
        name: outputTemplateData?.name,
        description: newDescription,
      },
    });
  };

  const handleSetAsDefaultClick = (variantNumber: number) => {
    setOutputTemplateAsDefault({
      variables: {
        outputTemplateId,
        variantNumber,
      },
      optimisticResponse: () => ({
        updateLlmOutputTemplateVariants: {
          returning: [
            {
              __typename: 'LlmOutputTemplateVariants' as const,
              outputTemplateId,
              variantNumber,
              isCurrentDefault: false,
            },
          ],
        },
        updateLlmOutputTemplateVariantsByPk: {
          __typename: 'LlmOutputTemplateVariants' as const,
          outputTemplateId,
          variantNumber,
          isCurrentDefault: true,
        },
      }),
    });
  };

  const handleUpdateVariantNotes = async (newNotes: string) => {
    if (selectedVariantNumber === null) return;

    await updateVariantNotes({
      variables: {
        outputTemplateId,
        variantNumber: selectedVariantNumber,
        variantNotes: newNotes || null,
      },
    });
  };

  const { exportableJson, hydratedTemplate, outputTypeLabel, jsonSchema } =
    useMemo(() => {
      if (!selectedVariantTemplate) {
        return {
          exportableJson: undefined,
          hydratedTemplate: undefined,
          outputTypeLabel: undefined,
          jsonSchema: undefined,
        };
      }

      const outputType =
        outputTemplateData?.llmOutputType ?? LlmOutputTypesLookup.SOAP_NOTE;
      const exportContent = {
        type: 'hydrateArtifact',
        artifactTemplate: selectedVariantTemplate,
        outputType,
        isImported: Boolean(
          outputTemplateData?.external_chart_templates?.length,
        ),
      };

      const templateJsonSchema = convertDynamicArtifactToJsonSchema({
        name: outputTemplateData?.name ?? '',
        description: outputTemplateData?.description ?? '',
        blocks: selectedVariantTemplate.blocks,
      }).valueOf();

      return {
        exportableJson: JSON.stringify(exportContent, null, 2),
        hydratedTemplate: hydrateDynamicArtifactTemplatePreview(
          selectedVariantTemplate,
        ),
        outputTypeLabel: startCase(
          outputType.split('_').join(' ').toLowerCase(),
        ),
        jsonSchema: templateJsonSchema,
      };
    }, [
      selectedVariantTemplate,
      outputTemplateData?.llmOutputType,
      outputTemplateData?.external_chart_templates?.length,
      outputTemplateData?.name,
      outputTemplateData?.description,
    ]);

  const handleOnCopyClick = async (data: string) => {
    try {
      await navigator.clipboard.writeText(data);
      toast.success('Variant JSON copied to clipboard.');
    } catch {
      toast.error('Variant JSON could not be copied.');
    }
  };

  return (
    <>
      {isOrganizationRoute && (
        <Header>
          <SidebarTrigger />
          <PageTitle>Templates</PageTitle>
        </Header>
      )}
      <Content padded>
        <VStack gap={8}>
          <VStack>
            <InlineEdit
              value={outputTemplateData?.name ?? ''}
              onSave={handleUpdateName}
              isLoading={isUpdating}
              isReadOnly={!(isEluveAdmin || isTemplateCreator)}
              placeholder="Template name"
              className="mt-4 w-full"
            >
              <Heading size="md" weight="semibold">
                {outputTemplateData?.name}
              </Heading>
              {outputTypeLabel && (
                <Tag
                  color="gray"
                  label={outputTypeLabel}
                  size="s"
                  subtle={false}
                />
              )}
            </InlineEdit>
            <InlineEdit
              value={outputTemplateData?.description ?? ''}
              onSave={handleUpdateDescription}
              isLoading={isUpdating}
              isReadOnly={!(isEluveAdmin || isTemplateCreator)}
              multiline
              placeholder="Template description"
              className="w-full text-sm"
            >
              <Text className="line-clamp-2">
                {outputTemplateData?.description}
              </Text>
            </InlineEdit>
          </VStack>
          <HStack gap={8} align="stretch">
            <VStack className="flex-1 sm:min-w-60 md:min-w-72">
              <Heading size="sm">Variants</Heading>
              {(outputTemplateData?.variants ?? []).map((variant) => (
                <TemplateVariantCard
                  isSelected={variant.variantNumber === selectedVariantNumber}
                  key={variant.variantNumber}
                  onClick={() => {
                    navigate(
                      `${outputTemplateBasePath}/${outputTemplateId}/variant/${variant.variantNumber}`,
                    );
                  }}
                  onDuplicateClick={() => {
                    navigate(
                      createCloneOutputVariantPath(variant.variantNumber),
                    );
                  }}
                  onVariantEditClick={() => {
                    navigate(createEditVariantPath(variant.variantNumber));
                  }}
                  onSetAsDefaultClick={
                    !(isEluveAdmin || isTemplatePublisher)
                      ? undefined
                      : () => {
                          handleSetAsDefaultClick(variant.variantNumber);
                        }
                  }
                  variant={{
                    ...variant,
                    createdBy: variant.creator
                      ? formatHumanName(
                          variant.creator.firstName,
                          variant.creator.lastName,
                        )
                      : undefined,
                    isDraft: variant.isDraft,
                  }}
                  viewOnly={!(isEluveAdmin || isTemplateCreator)}
                />
              ))}
            </VStack>
            <Separator orientation="vertical" />
            <VStack>
              <HStack justify="between">
                <Heading size="sm">Preview</Heading>
                <HStack wFull={false}>
                  {isOrganizationRoute && (
                    <TemplatePublishingDialog
                      templateId={outputTemplateData!.id}
                      templateName={outputTemplateData!.name}
                      isCompact={false}
                    />
                  )}
                  {exportableJson && (
                    <Drawer>
                      <DrawerTrigger asChild>
                        <NewButton
                          icon={{ name: 'ClipboardPaste' }}
                          text="Export"
                          type="outline"
                        />
                      </DrawerTrigger>
                      <DrawerContent>
                        <VStack className="container h-[75vh]" gap={4}>
                          <HStack justify="between">
                            <Heading size="lg">Variant JSON</Heading>
                            <NewButton
                              onClick={() => handleOnCopyClick(exportableJson)}
                              icon={{ name: 'Copy' }}
                              text="Copy to clipboard"
                            />
                          </HStack>
                          <Textarea
                            value={exportableJson}
                            className="mb-4 w-full grow border font-mono"
                            readOnly
                          />
                        </VStack>
                      </DrawerContent>
                    </Drawer>
                  )}
                  {jsonSchema && (
                    <Drawer>
                      <DrawerTrigger asChild>
                        <NewButton
                          icon={{ name: 'Braces' }}
                          text="View JSON Schema"
                          type="outline"
                        />
                      </DrawerTrigger>
                      <DrawerContent>
                        <VStack className="container h-[75vh]" gap={4}>
                          <HStack justify="between">
                            <Heading size="lg">
                              Output Template JSON Schema
                            </Heading>
                            <NewButton
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(
                                    JSON.stringify(jsonSchema),
                                  );
                                  toast.success(
                                    'JSON Schema copied to clipboard.',
                                  );
                                } catch {
                                  toast.error(
                                    'JSON Schema could not be copied.',
                                  );
                                }
                              }}
                              icon={{ name: 'Copy' }}
                              text="Copy to clipboard"
                            />
                          </HStack>
                          <DebugJson value={jsonSchema} />
                        </VStack>
                      </DrawerContent>
                    </Drawer>
                  )}
                </HStack>
              </HStack>
              <DynamicArtifactRenderer
                blocks={hydratedTemplate?.blocks ?? []}
                isReadonly
              />
              <>
                <Separator />
                <VStack gap={2}>
                  <Heading size="sm">Variant Notes</Heading>
                  <InlineEdit
                    value={
                      outputTemplateData?.variants.find(
                        (variant) =>
                          variant.variantNumber === selectedVariantNumber,
                      )?.variantNotes ?? ''
                    }
                    onSave={handleUpdateVariantNotes}
                    isReadOnly={!(isEluveAdmin || isTemplateCreator)}
                    placeholder="No notes."
                    multiline
                    className="w-full"
                  >
                    <Text
                      size="sm"
                      color="secondary"
                      className="whitespace-pre-wrap italic"
                    >
                      {outputTemplateData?.variants.find(
                        (variant) =>
                          variant.variantNumber === selectedVariantNumber,
                      )?.variantNotes || 'No notes.'}
                    </Text>
                  </InlineEdit>
                </VStack>
              </>
            </VStack>
          </HStack>
        </VStack>{' '}
      </Content>
    </>
  );
};
